import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button";
import { DropArea } from "@/components/DropArea";
import { MeshField } from "@/components/MeshField";
import { useDemoRun } from "@/store/demoRun";
import { DEFAULT_START_SHAPE, useOutlineDraft } from "@/store/outlineDraft";

import styles from "./EntryAreas.module.css";
import { MailNotice } from "./MailNotice";
import type { MailDrop } from "./useMailDrop";

/**
 * The neutral starting rectangle — a seed, not a claim about the house
 * (design §C-5). It is the same fallback `/umriss` already falls back to, so
 * the two figures that used to be typed here were never anything but a
 * default with a form around it.
 */
const NEUTRAL = { widthMm: 10_000, depthMm: 8_000 };

interface EntryAreasProps {
  /**
   * The start page has a list above (UX gate round 1, #1): both areas shrink
   * to a band of ≈ 160 px — title, the one sentence that has to stand before
   * a drop, the action — and the mesh withdraws to a strip at the right
   * edge, where no text lies on it. Full height only on a first visit.
   */
  compact?: boolean;
  /**
   * A file is being dragged somewhere over the page. The left area lifts, the
   * right one steps back (design §6, §8) — the state is the *page's*, because
   * the list beside these two areas dims with them, so `StartScreen` owns the
   * listener and hands the answer down.
   */
  fileHovering?: boolean;
  /**
   * The mail's way in (`useMailDrop`, owned by the page so a mail let go
   * anywhere on it reaches the same state). With it the left area takes a mail
   * as well as a drawing and the filing's messages stand under the two areas;
   * without it the area reads drawings only, as before.
   */
  mail?: MailDrop;
}

/**
 * The mesh, held in its own box so it can withdraw to an edge (compact) —
 * the box, not the field, carries the red sheen, so both stay inside it.
 */
function Strip() {
  return (
    <div className={styles.strip} aria-hidden="true">
      <MeshField className={styles.mesh} />
    </div>
  );
}

/**
 * The two ways into a Vorgang, side by side and of equal rank (design §6).
 *
 * Equal rank is the decision C-4, and it is a **named departure from E-45 in
 * the presentation only**: drawing by hand stays the fallback in substance —
 * nothing here reads a drawing that was not handed over — but it is no longer
 * shown as the smaller door. Equality is expressed by sameness: both areas
 * are the sign-in screen's steel, both carry the mesh, both are the same size
 * (C-8).
 */
export function EntryAreas({ compact = false, fileHovering = false, mail }: EntryAreasProps) {
  const navigate = useNavigate();
  const setSeed = useDemoRun((state) => state.setSeed);
  const beginShape = useOutlineDraft((state) => state.beginShape);

  const drawButton = (
    <Button
      className={styles.draw}
      // A mail is being filed: its Vorgang is about to open, a second one
      // started here would be left behind by that navigation.
      disabled={mail?.busy ?? false}
      onClick={() => {
        setSeed(NEUTRAL);
        // A fresh start every time somebody starts here — one wall, the
        // most frequent request (Task 17); `/umriss` offers the
        // rectangle beside it. The drawing screen keeps whatever is
        // under way, and this is the one place that is allowed to throw
        // it away. The copy the browser kept is not thrown away with it:
        // `/umriss` offers it back, so a drawing lost to a crash returns
        // through this button.
        beginShape(DEFAULT_START_SHAPE);
        navigate("/umriss");
      }}
    >
      Umriss zeichnen
    </Button>
  );

  // Under the drop box, in both heights (B-9, spec 2026-10-22 §7): the hint
  // is about bringing documents in, and under the button of „Umriss zeichnen“
  // it read as a part of drawing. The mail does not need this card — it is
  // taken wherever on the page it is let go — so this is only where it is
  // *said*. Only said where there is a mail path to say it of, and not on a
  // screen that cannot drag (`.mailHint`).
  const mailHint = mail ? (
    <p className={styles.mailHint}>Mit Kundenmail? Zieh sie einfach auf die Seite.</p>
  ) : null;

  // One heading over both (UX gate round 1, #1): compact, the pair stands
  // under „Zuletzt bearbeitet" as a section of its own, „Neuer Vorgang", and
  // the two kickers („Mit/Ohne Zeichnung") give way to it. At full height
  // the kickers speak and the heading is there for a screen reader only —
  // either way the two titles sit one level under it.
  return (
    <section className={styles.entry} aria-labelledby="new-case">
      <h2 id="new-case" className={compact ? `label ${styles.entryHead}` : "visually-hidden"}>
        {/* The mesh marker before a section title (design 2026-10-24 §8.6). */}
        <span className="marke" aria-hidden="true" />
        Neuer Vorgang
      </h2>
      <div
        className={`${styles.areas} ${compact ? styles.compact : ""} ${fileHovering ? styles.lifted : ""}`}
        data-compact={compact ? "" : undefined}
      >
        <section
          className={`${styles.area} ${styles.drawing}`}
          aria-labelledby="area-drawing"
          data-lifted={fileHovering ? "" : undefined}
        >
          <Strip />
          {/* Kicker, Titel und Zeile gehen *in* die Ablage hinein — deren Wurzel
              trägt die Ziehgriffe, und §6 verlangt die ganze Fläche als Ziel.
              Eine Zeichnung, die über der Überschrift losgelassen wird, landet
              damit genauso wie eine im gestrichelten Kasten. */}
          <div className={styles.body}>
            <DropArea
              compact={compact}
              onMail={mail ? (files) => void mail.receive(files) : undefined}
              disabled={mail?.busy ?? false}
              heading={
                <div className={styles.heading}>
                  {compact ? null : <p className={`label ${styles.kicker}`}>Mit Zeichnung</p>}
                  <h3 id="area-drawing" className={styles.title}>
                    Zeichnung einlesen
                  </h3>
                  {/* Compact, the sentence that has to stand before a drop
                      (AN-08, `DropArea`'s note) says enough; the kind of file
                      is on the refusal of a wrong one. */}
                  {compact ? null : (
                    <p className={styles.line}>Handskizze, Foto, PDF — oder gleich die Kundenmail.</p>
                  )}
                </div>
              }
            />
            {mailHint}
          </div>
        </section>

        <section className={`${styles.area} ${styles.hand}`} aria-labelledby="area-hand">
          <Strip />
          <div className={styles.body}>
            {compact ? null : <p className={`label ${styles.kicker}`}>Ohne Zeichnung</p>}
            <h3 id="area-hand" className={styles.title}>
              Umriss zeichnen
            </h3>
            <p className={styles.line}>Eine Wand oder ein Rechteck — Ecken ziehen, Maße tippen.</p>
            {/* One button, no fields (C-5). Breite/Tiefe were only ever a seed
                for the starting rectangle, and `/umriss` holds the same one.
                White on the steel like „Dateien auswählen" beside it (UX gate
                round 1, #13): one primary colour in the app — ink on paper,
                its inverse on anthracite, where ink would vanish (1.5:1) —
                and two equal ways in look equal (C-8). */}
            {/* At full height the button stands in a frame of its own, the
                left area's box in solid line (UX gate round 4, #12): beside
                a drop box of 175 px the button alone left 214 px of empty
                steel under it. The mesh fills the frame as it fills the
                empty drop box — the board is empty until the outline is
                drawn. Solid, not dashed: nothing is dropped here.
                Compact, the button stands alone at the foot of the card,
                level with the drop box's action row beside it (B-9, spec
                2026-10-22 §7): the box that repeated the card's title as
                „Von Hand zeichnen" beside a second word said nothing the
                heading above does not. The mail's hint is not here any more
                — it stands under the drop box on the left. */}
            {compact ? (
              drawButton
            ) : (
              <div className={styles.board} data-board="">
                {drawButton}
              </div>
            )}
          </div>
        </section>
      </div>
      {mail ? <MailNotice mail={mail} /> : null}
    </section>
  );
}
